// ReplyComposer.tsx — inline reply input. Stateful for its own draft; reports changes upward.
import { useState } from 'react';
import { useReplyDraft } from '@/hooks/useReplyDraft';
import type { ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@/ui';

export interface ReplyComposerProps {
  draftScope?: string;
  /** Handle being replied to (for the "Replying to @x" header); null = direct top-level reply. */
  replyingToHandle: string | null;
  onSubmit: (body: string) => void | Promise<void>;
  onCancel: () => void;
  /** Notifies the parent of the current draft (for the discard guard). */
  onDraftChange: (draft: string) => void;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 8px 54px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Mention = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
`;

const Input = styled.textarea`
  width: 100%;
  min-height: 48px;
  resize: none;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:focus-visible {
    border-bottom-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export function ReplyComposer({ replyingToHandle, onSubmit, onCancel, onDraftChange, draftScope }: ReplyComposerProps) {
  const { value, setValue, saved, restore, discard } = useReplyDraft(draftScope);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const trimmed = value.trim();

  function change(e: ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value);
    onDraftChange(e.target.value);
  }

  async function submit() {
    if (!trimmed || busy) return;
    setBusy(true); setError('');
    try { await onSubmit(trimmed); discard(); }
    catch { setError('Could not reply. Your draft is still here. Try again.'); }
    finally { setBusy(false); }
  }

  function keydown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <Wrap>
      {saved && <div role="status">You have an unfinished reply. <Button variant="ghost" onClick={() => { restore(); onDraftChange(saved); }}>Restore draft</Button><Button variant="ghost" onClick={discard}>Discard</Button></div>}
      {error && <p role="alert">{error}</p>}
      <Head>
        <span>
          {replyingToHandle ? (
            <>
              Replying to <Mention>{replyingToHandle}</Mention>
            </>
          ) : (
            'Add a reply'
          )}
        </span>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </Head>
      <Input
        value={value}
        disabled={busy}
        onChange={change}
        onKeyDown={keydown}
        placeholder="Add a reply…"
        aria-label="Reply"
        autoFocus
      />
      <Actions>
        <Button variant="primary" size="sm" disabled={!trimmed || busy} onClick={() => void submit()}>
          Post
        </Button>
      </Actions>
    </Wrap>
  );
}
